import { useEffect } from "react";
import { DatePicker, Button, Input, Tooltip } from "antd";
import { Select } from "./sharedImports";
import RenderRecurrenceOptions from "./RenderRecurrenceOptions";
import dayjs from "dayjs";
import dayjsUtc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useFormik } from "formik";
import { formatCamelCaseReg } from "../../../../../shared/sharedConstants";
import { useIncidentList } from "../../../IncidentsList/utils";
import {
  emailSubscriptionValidationSchema,
  getInitialValues,
} from "./useFormManagement";
import { toast } from "react-toastify";
import { InfoCircleOutlined } from "@ant-design/icons";

dayjs.extend(dayjsUtc);
dayjs.extend(timezone);
dayjs.tz.setDefault(dayjs.tz.guess());

interface EmailSubscriptionFormProps {
  handleSubmit: (formData: any) => void;
  mode?: "edit" | "add";
  selectedSubscription?: any;
  handleClose: () => void;
  isLoading?: boolean;
  isFacility: boolean;
  title: string;
}

function EmailSubscriptionForm({
  handleSubmit,
  mode = "add",
  selectedSubscription,
  handleClose,
  isLoading,
  isFacility,
  title,
}: Readonly<EmailSubscriptionFormProps>) {
  const recurrenceOptions = ["Daily", "Weekly", "Monthly"];
  const periodOptions = [
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "months", label: "Months" },
  ];
  const dateFormat = "YYYY-MM-DD";

  const { facilities } = useIncidentList() as any;

  const {
    setFieldValue,
    values,
    errors,
    handleChange,
    handleSubmit: handleFormSubmit,
    handleBlur,
  } = useFormik({
    initialValues: getInitialValues(selectedSubscription, dateFormat),
    validationSchema: emailSubscriptionValidationSchema(isFacility),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      const formData: any = {
        ...selectedSubscription,
        ...values,
        startDate: dayjs(values.startDate).format("YYYY-MM-DD"),
      };

      if (values.endDate) {
        formData.endDate = dayjs(values.endDate).format("YYYY-MM-DD");
      }

      if (values.recurrence === "weekly") {
        formData.weeklyDays = values.weeklyDays;
      }
      if (values.recurrence === "monthly") {
        formData.monthlyDay = values.monthlyDay;
      }
      handleSubmit(formData);
    },
  });

  useEffect(() => {
    if (selectedSubscription) {
      setFieldValue(
        "recurrence",
        selectedSubscription?.Interval_Type__c.toLowerCase()
      );
      setFieldValue("periodType", selectedSubscription?.Trailing_Period__c);
    }
  }, [selectedSubscription]);

  useEffect(() => {
    if (values.recurrence === "monthly") {
      setFieldValue("monthlyDay", 1);
    }
  }, [values.recurrence]);

  const handlePeriodLengthChange = (e: any) => {
    const value = e.target.value;
    let max = 365; // default max if days

    if (values.periodType === "Weeks") max = 52;
    if (values.periodType === "Months") max = 12;

    if (value && Number(value) > max) {
      toast.error(
        `For ${values.periodType || "Days"}, the max allowed is ${max}.`
      );
    }
    setFieldValue("periodLength", value);
  };

  const renderTooltip = () => {
    return (
      <Tooltip title="Choosing 'Months' for 'Period Type' and '2' for 'Period Length' will give you the last two months of data each time the report is run.">
        <InfoCircleOutlined className="ml-1 cursor-pointer text-gray-500" />
      </Tooltip>
    );
  };

  return (
    <div className="mx-auto w-full max-w-3xl overflow-y-scroll">
      <div>
        <header className="text-purple-500">
          {formatCamelCaseReg(mode)} Email Subscription
        </header>
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div>
            <p className="block text-sm font-medium text-gray-700">
              Report: {title}
              <span className="font-bold">
                {mode === "edit" &&
                  selectedSubscription?.Facility__c &&
                  ` For ${
                    facilities.find(
                      (facility: any) =>
                        facility.value === selectedSubscription.Facility__c
                    )?.label || ""
                  }`}
              </span>
            </p>
          </div>
          <div className="mb-10">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-500"
            >
              Description<span className="text-red-500">*</span>
            </label>
            <Input
              className="mt-1 rounded-none border border-solid border-purple-100"
              id="description"
              name="description"
              placeholder="New Subscription"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.description && (
              <div className="text-red-500">{errors.description as string}</div>
            )}
          </div>
          <div className="bg-slate-50">
            <div className="mt-10 flex flex-col border-0 transition-all duration-300 sm:flex-row sm:gap-5">
              <Button
                style={{
                  border: "1px solid #B580FF26",
                }}
                className={`border-1 shadow-custom-expanded flex h-12 w-full items-center justify-between border-b-0 bg-purple-100 p-4 text-left text-purple-400 shadow-lg shadow-purple-100 transition-all duration-300 ease-in-out hover:text-purple-400`}
              >
                <span>Schedule</span>
              </Button>
            </div>
            <div
              style={{ border: "1px solid #B580FF26" }}
              className={`border-1 shadow-custom-normal max-h-[1000px] rounded-sm border-t-0 bg-white-main opacity-100`}
            >
              <div className="border-t p-4">
                <div className="space-y-4">
                  <div className="bg-white-main">
                    <div className="border-1 mt-3 flex flex-col space-y-4 divide-stone-400 rounded-sm bg-white-main px-3 py-3">
                      <div className="mr-10">
                        <label className="flex justify-between text-sm font-medium text-gray-700">
                          Recurrence
                        </label>
                        <div className="flex flex-row gap-4">
                          {recurrenceOptions.map((option) => (
                            <div key={option}>
                              <label className="mb-4 mt-4 inline-flex cursor-pointer items-center">
                                <input
                                  type="radio"
                                  className="checked:white h-6 w-6 cursor-pointer border-slate-400 text-purple-500 accent-purple-500 outline-none checked:text-xl"
                                  name="recurrence"
                                  value={option.toLowerCase()}
                                  checked={
                                    values.recurrence === option.toLowerCase()
                                  }
                                  onChange={() => {
                                    setFieldValue(
                                      "recurrence",
                                      option.toLowerCase()
                                    );
                                  }}
                                />
                                <span className="ml-2">{option}</span>
                              </label>
                              {errors.recurrence && (
                                <div className="text-red-500">
                                  {errors.recurrence as string}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="">
                        <div className="mb-3 flex">
                          <RenderRecurrenceOptions
                            recurrence={values.recurrence}
                            weeklyDays={values.weeklyDays}
                            monthlyDay={values.monthlyDay}
                            errors={errors}
                            setFieldValue={setFieldValue}
                          />
                        </div>
                        <div className="flex items-center">
                          <div className="mb-4 flex flex-col">
                            <label
                              htmlFor="startDate"
                              className="text-sm font-medium text-gray-700"
                            >
                              Starting On{""}
                              <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                              className="w-full lg:w-[250px]"
                              format={dateFormat}
                              onChange={(date) =>
                                setFieldValue("startDate", date)
                              }
                              defaultValue={
                                selectedSubscription?.Start_Date__c
                                  ? dayjs(
                                      selectedSubscription?.Start_Date__c,
                                      dateFormat
                                    )
                                  : undefined
                              }
                              status={errors.startDate ? "error" : ""}
                              minDate={dayjs()}
                            />
                          </div>
                          <div className="mb-4 ml-4 flex flex-col">
                            <label
                              htmlFor="endDate"
                              className="text-sm font-medium text-gray-700"
                            >
                              Ending On {""}
                              <span className="text-xs">{"(optional)"}</span>
                            </label>
                            <DatePicker
                              className="w-full lg:w-[250px]"
                              format={dateFormat}
                              onChange={(date) =>
                                setFieldValue("endDate", date)
                              }
                              defaultValue={
                                selectedSubscription?.End_Date__c
                                  ? dayjs(
                                      selectedSubscription?.End_Date__c,
                                      dateFormat
                                    )
                                  : undefined
                              }
                              status={errors.endDate ? "error" : ""}
                              minDate={dayjs()}
                            />
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="flex w-full space-x-4">
                            <div>
                              <label
                                htmlFor="periodType"
                                className="text-sm font-medium text-gray-700"
                              >
                                Period Type{""}
                                <span className="text-red-500">*</span>
                                {renderTooltip()}
                              </label>
                              <Select
                                className="w-[150px] lg:w-[250px]"
                                onChange={(val: any) =>
                                  setFieldValue("periodType", val?.label)
                                }
                                options={periodOptions}
                                defaultValue={periodOptions.find(
                                  (option) => option.label === values.periodType
                                )}
                                name="periodType"
                              />
                              {errors.periodType && (
                                <div className="text-red-500">
                                  {errors.periodType as string}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col">
                              <label
                                htmlFor="periodLength"
                                className="font-medium text-gray-700"
                              >
                                Period Length{""}
                                <span className="text-red-500">*</span>
                                {renderTooltip()}
                              </label>
                              <input
                                placeholder="Enter period length"
                                type="number"
                                className="w-full lg:w-[250px]"
                                name="periodLength"
                                value={values.periodLength}
                                onChange={handlePeriodLengthChange}
                                style={{
                                  height: "2.39rem",
                                  border: "0.5px solid gray",
                                  borderRadius: "3px",
                                  textIndent: "10px",
                                }}
                                disabled={!values.periodType}
                              />
                              {errors.periodLength && (
                                <div className="text-red-500">
                                  {errors.periodLength as string}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {!isFacility && mode === "add" && (
                          <div className="mt-4 space-y-4 border-t">
                            <div className="flex">
                              <div className="flex-1">
                                <label
                                  htmlFor="facility"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Facility{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Select
                                  onChange={(val: any) =>
                                    setFieldValue("facility", val?.value)
                                  }
                                  options={facilities?.sort((a: any, b: any) =>
                                    a.label > b.label ? 1 : -1
                                  )}
                                />

                                {errors.facility && (
                                  <div className="text-red-500">
                                    {errors.facility as string}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={() => handleFormSubmit()}
              className="cursor-pointer border-0 bg-purple-400 px-4 py-2 text-white-main transition-colors hover:bg-purple-500"
              loading={isLoading}
              disabled={isLoading}
            >
              {mode === "add" ? "Subscribe" : "Update"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmailSubscriptionForm;
